import { useEffect, useState } from 'react'
import apiClient from '../../../api/index.js'
import { asMessage } from '../../../shared/utils/errorDetail'

/**
 * PLATFORM ADMIN: a company's paid add-ons - and the one feature flag that is really a company
 * preference - on the company's features map (identity GET/PUT /branding/{id}/features).
 *
 * ONLY FLAGS THAT DO SOMETHING TODAY. identity routers/branding.py lists eight "entitlement" keys, but
 * seven of them (forms_platform, pdf_reader, inventory, fleet_management, office_board, managed_email,
 * llm_features) are read by nothing - those modules are switched on the Modules screen now. A box that
 * changes nothing is worse than no box, so they are not offered here; they stay in the backend list,
 * where they keep a company from setting them for itself. Add one here only once code reads it.
 *
 *   office_worker_ai - a PAID ADD-ON (only a platform admin can change it): gate 1 of the AI Office
 *     Worker's two AI gates (shared/agent_host/ai_gates.py). Gate 2 is the company's own consent.
 *   invoice_energy_transfer_format - the Energy Transfer "964" invoice layout
 *     (finance services/pdf_service.py reads this same map). A COMPANY PREFERENCE, not an entitlement:
 *     the company's own admins may change it too, and the panel says so.
 *
 * Only the boxes that changed are sent (the server merges), with the reason; the server records who
 * changed what, from what to what, in the same transaction.
 */
export const ADD_ONS = [
  {
    key: 'office_worker_ai',
    label: 'AI Office Worker - AI features',
    kind: 'Paid add-on',
    note: 'Lets this company\'s AI Office Worker use AI. It also needs the Office Worker switched on under '
      + 'Modules and the company\'s own admin to give AI consent.',
  },
  {
    key: 'invoice_energy_transfer_format',
    label: 'Energy Transfer (964) invoice layout',
    kind: 'Company preference',
    note: 'Not a paid add-on: the company\'s own admins can change this too. On, invoice packages for '
      + 'project 964 print in the Energy Transfer layout.',
  },
]

export default function TenantAddOns({ tenantId }) {
  const [stored, setStored] = useState(null)   // the company's features map as the server has it
  const [draft, setDraft] = useState({})
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await apiClient.get(`/branding/${tenantId}/features`)
        if (!cancelled) setStored(res.data?.features || {})
      } catch (err) {
        if (!cancelled) setMsg({ type: 'err', text: asMessage(err?.response?.data?.detail) || 'Could not load this company\'s add-ons.' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [tenantId])

  if (loading) return <p className="text-sm text-gray-500">Loading add-ons…</p>
  if (!stored) return <p className="text-sm text-red-600">{msg?.text}</p>

  const storedOn = (k) => stored[k] === true
  const value = (k) => (k in draft ? draft[k] : storedOn(k))
  const changed = Object.keys(draft).filter((k) => draft[k] !== storedOn(k))

  const toggle = (k) => {
    setMsg(null)
    const next = !value(k)
    setDraft((prev) => {
      const out = { ...prev }
      if (next === storedOn(k)) delete out[k]
      else out[k] = next
      return out
    })
  }

  const save = async () => {
    if (changed.length === 0) return
    setSaving(true)
    setMsg(null)
    try {
      const res = await apiClient.put(`/branding/${tenantId}/features`, {
        features: Object.fromEntries(changed.map((k) => [k, draft[k]])),
        note: note.trim() || null,
      })
      setStored(res.data?.features || {})
      setDraft({})
      setNote('')
      setMsg({ type: 'ok', text: 'Saved, and recorded with your name.' })
    } catch (err) {
      setMsg({ type: 'err', text: asMessage(err?.response?.data?.detail) || 'The change was not saved.' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        What this company has paid for, and one layout choice. Every change is recorded with your name.
      </p>
      <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
        {ADD_ONS.map((a) => {
          const on = value(a.key)
          return (
            <li key={a.key} className="flex items-start justify-between gap-4 p-3">
              <div>
                <div className="font-medium text-gray-900">{a.label}</div>
                <div className="text-xs font-semibold text-gray-500">{a.kind}</div>
                <div className="mt-1 text-xs text-gray-500">{a.note}</div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 shrink-0">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(a.key)}
                  disabled={saving}
                  aria-label={`${a.label} for this company`}
                />
                {on ? 'On' : 'Off'}
              </label>
            </li>
          )
        })}
      </ul>
      <label className="block text-sm text-gray-700">
        Why (optional, kept with the record)
        <input
          type="text"
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </label>
      {msg && <p className={`text-sm ${msg.type === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</p>}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving || changed.length === 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
